import { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AnimatePresence } from 'motion/react';

import { ThemeProvider } from '@/components/theme-provider';
import { CommandPalette } from '@/components/command-palette';
import { GlobalModals } from '@/components/global-modals';
import { ErrorBoundary } from '@/components/error-boundary';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import '@/lib/i18n';

// Lazy load pages
import { lazy } from 'react';

const LoginPage = lazy(() => import('@/features/auth/pages/login-page'));
const RegisterPage = lazy(() => import('@/features/auth/pages/register-page'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/forgot-password-page'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/reset-password-page'));
const DashboardPage = lazy(() => import('@/features/dashboard/pages/dashboard-page'));
const StudyPlanPage = lazy(() => import('@/features/study-plan/pages/study-plan-page'));
const StudentsPage = lazy(() => import('@/features/students/pages/students-page'));
const StudentDetailPage = lazy(() => import('@/features/students/pages/student-detail-page'));
const TasksPage = lazy(() => import('@/features/tasks/pages/tasks-page'));
const TaskDetailPage = lazy(() => import('@/features/tasks/pages/task-detail-page'));
const CalendarPage = lazy(() => import('@/features/calendar/pages/calendar-page'));
const SettingsPage = lazy(() => import('@/features/settings/pages/settings-page'));
const ProfilePage = lazy(() => import('@/features/profile/pages/profile-page'));
const GradesPage = lazy(() => import('@/features/grades/pages/grades-page'));

// Layout
const MainLayout = lazy(() => import('@/components/layout/main-layout'));

// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 2,
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Loading fallback
function LoadingFallback() {
  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">Cargando...</p>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, token, refreshSession } = useAuthStore();
  const [isRefreshing, setIsRefreshing] = useState(() => !!token && !isAuthenticated);

  useEffect(() => {
    if (token && !isAuthenticated) {
      setIsRefreshing(true);
      refreshSession()
        .catch((error) => {
          console.error('Failed to refresh session:', error);
        })
        .finally(() => {
          setIsRefreshing(false);
        });
    }
  }, [token, isAuthenticated, refreshSession]);

  if (isRefreshing) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public route wrapper (redirect if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { language } = useUIStore();

  // Update document language
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <AnimatePresence mode="wait">
              <Routes>
                {/* Public routes */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <PublicRoute>
                      <RegisterPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <PublicRoute>
                      <ForgotPasswordPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <PublicRoute>
                      <ResetPasswordPage />
                    </PublicRoute>
                  }
                />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<DashboardPage />} />
                  <Route path="study-plan" element={<StudyPlanPage />} />
                  <Route path="students" element={<StudentsPage />} />
                  <Route path="students/:id" element={<StudentDetailPage />} />
                  <Route path="tasks" element={<TasksPage />} />
                  <Route path="tasks/:id" element={<TaskDetailPage />} />
                  <Route path="calendar" element={<CalendarPage />} />
                  <Route path="grades" element={<GradesPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="profile" element={<ProfilePage />} />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AnimatePresence>
          </Suspense>
          </ErrorBoundary>
          <CommandPalette />
          <GlobalModals />
        </BrowserRouter>

        <Toaster
          position="top-right"
          expand
          richColors
          closeButton
          toastOptions={{
            duration: 4000,
          }}
        />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
